import React, { useCallback, useState, useEffect } from 'react';
import SignaturePad from 'react-signature-canvas';
import { ClearIcon } from 'assets/icons';
import {
  IconButton,
  Typography,
  Box,
  Stack,
  TextField,
  Dialog,
  DialogContent,
} from '@mui/material';
import MuiButton from 'components/__v5__/Button/MuiButton';
import { grey } from 'assets/colors/colors';

interface SignatureCanvasModalProps {
  isDialogOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
  save: (signature: string) => void;
  setName?: (name: string) => void;
  sigCanvas: React.RefObject<SignaturePad>;
  clear: () => void;
  multi?: boolean;
}

export const SignatureCanvas: React.FC<SignatureCanvasModalProps> = ({
  sigCanvas,
  clear,
  save,
  setIsDialogOpen,
  setName,
  isDialogOpen,
  multi = false,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [signatureName, setSignatureName] = useState<string>('');

  // Reset state when dialog opens
  useEffect(() => {
    if (isDialogOpen) {
      setSignatureName('');
      setErrorMessage(null);
    }
  }, [isDialogOpen]);

  const handleSave = useCallback(() => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      setErrorMessage('Please provide a signature.');
      return;
    }

    if (multi) {
      if (!signatureName.trim()) {
        setErrorMessage('Please add a name.');
        return;
      }
      setName?.(signatureName);
    }

    save(signatureName);
    setIsDialogOpen(false);
  }, [sigCanvas, save, setIsDialogOpen, setName, signatureName, multi]);

  return (
    <Dialog open={isDialogOpen} fullWidth>
      <DialogContent>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Sign here</Typography>
          <IconButton onClick={() => setIsDialogOpen(false)}>
            <ClearIcon />
          </IconButton>
        </Stack>

        {/* Signature Canvas */}
        <Box sx={{ background: grey.g200, mb: 2, p: 1 }}>
          <SignaturePad
            ref={sigCanvas}
            canvasProps={{
              className: 'signatureCanvas',
              height: 250,
              width: 550,
            }}
          />
        </Box>

        {/* Clear Button */}
        <MuiButton variant="outlined" color="secondary" onClick={clear}>
          Clear
        </MuiButton>

        {/* Signature Agreement */}
        <Typography variant="body2" my={2}>
          By signing above, I certify that this signature is authentic and
          represents my genuine consent and agreement.
        </Typography>

        {/* Signatory Name (Optional) */}
        {multi && (
          <TextField
            placeholder="Signatory Name"
            variant="outlined"
            fullWidth
            value={signatureName}
            onChange={(e) => {
              setSignatureName(e.target.value);
              if (errorMessage) setErrorMessage(null);
            }}
            sx={{ mb: 2 }}
          />
        )}

        {/* Error Message */}
        {errorMessage && (
          <Typography variant="body2" color="error">
            {errorMessage}
          </Typography>
        )}

        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
          <MuiButton
            variant="outlined"
            color="secondary"
            onClick={() => setIsDialogOpen(false)}
            size="small"
          >
            Cancel
          </MuiButton>
          <MuiButton variant="contained" color="primary" onClick={handleSave}>
            Save
          </MuiButton>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
